<div class="live-session-page">
  <!-- Header -->
  <div class="session-header">
    <button class="back-btn" (click)="goBack()">
      <i class="fa fa-arrow-left"></i>
      {{ 'liveSession.back' | translate }}
    </button>

    <div class="header-center">
      <h1 class="session-title">{{ schedule?.schedule_name || 'Live Session' }}</h1>
      <div class="progress-indicator">
        <span class="progress-text">{{ currentPosition }} {{ 'liveSession.of' | translate }} {{ totalEntries }}</span>
        <div class="progress-bar">
          <div class="progress-fill" [style.width.%]="progressPercent"></div>
        </div>
      </div>
    </div>

    <div class="header-actions">
      <button class="end-btn" (click)="endSession()" [disabled]="sessionEnded || processing">
        <i class="fa fa-stop-circle"></i>
        {{ 'liveSession.endSession' | translate }}
      </button>
    </div>
  </div>

  @if (loading) {
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ 'liveSession.loading' | translate }}</p>
    </div>
  } @else if (sessionEnded) {
    <div class="session-ended">
      <div class="ended-icon">
        <i class="fa fa-flag-checkered"></i>
      </div>
      <h2>{{ 'liveSession.sessionComplete' | translate }}</h2>
      <p>{{ 'liveSession.allParticipantsFinished' | translate }}</p>
      <button class="return-btn" (click)="goBack()">
        {{ 'liveSession.returnToSchedule' | translate }}
      </button>
    </div>
  } @else {
    <!-- Results Board View -->
    <div class="results-board">

      <!-- Round Tabs & Progress -->
      <div class="results-toolbar">
        <div class="round-tabs">
          @for (round of rounds; track round.round_id) {
            <button
              class="round-tab"
              [class.active]="selectedRound?.round_id === round.round_id"
              (click)="selectRound(round)">
              {{ round.round_name || 'Round ' + round.round_number }}
            </button>
          }
        </div>
        <div class="progress-info">
          <span class="progress-count">{{ completedCount }}/{{ totalParticipants }}</span>
          <span class="progress-label">{{ 'liveSession.completed' | translate }}</span>
        </div>
      </div>

      <!-- Results Table -->
      <div class="results-table-container">
        <table class="results-table">
          <thead>
            <tr>
              <th class="col-rank">#</th>
              <th class="col-rider">{{ 'liveSession.rider' | translate }}</th>
              <th class="col-result">{{ isTimeBased ? ('liveSession.time' | translate) : ('liveSession.points' | translate) }}</th>
              <th class="col-notes">{{ 'liveSession.notes' | translate }}</th>
              <th class="col-image">{{ 'liveSession.image' | translate }}</th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            @for (entry of entries; track entry.entry_id) {
              @if (isBreak(entry)) {
                <!-- Break Row -->
                <tr class="result-row break-row"
                    [class.is-current]="entry.entry_id === currentEntry?.entry_id">
                  <td class="col-rank">
                    <span class="start-order">#{{ entry.position }}</span>
                  </td>
                  <td class="col-rider break-cell" colspan="4">
                    <div class="break-content">
                      <i class="fa fa-coffee break-icon"></i>
                      <span class="break-name">{{ getParticipantName(entry) }}</span>
                      <span class="break-duration">{{ getBreakDuration(entry) }}</span>
                    </div>
                  </td>
                  <td class="col-actions">
                    @if (entry.entry_id === currentEntry?.entry_id) {
                      <button class="next-btn" (click)="goNext()" [disabled]="!canGoNext">
                        {{ 'liveSession.endBreak' | translate }}
                        <i class="fa fa-chevron-right"></i>
                      </button>
                    }
                  </td>
                </tr>
              } @else {
                <!-- Participant Row -->
                <tr class="result-row"
                    [class.is-current]="entry.entry_id === currentEntry?.entry_id"
                    [class.has-result]="hasResult(entry)"
                    [class.is-scratched]="entry.status === 'scratched'">

                  <!-- Position/Rank Column -->
                  <td class="col-rank">
                    @if (getPosition(entry)) {
                      <span class="rank-badge"
                            [class.rank-1]="getPosition(entry) === 1"
                            [class.rank-2]="getPosition(entry) === 2"
                            [class.rank-3]="getPosition(entry) === 3">
                        {{ getPosition(entry) }}
                      </span>
                    } @else {
                      <span class="start-order">#{{ entry.position }}</span>
                    }
                  </td>

                  <!-- Rider Info -->
                  <td class="col-rider">
                    <span class="rider-name">{{ getParticipantName(entry) }}</span>
                    <span class="rider-horse">{{ getHorseName(entry) }}</span>
                  </td>

                  <!-- Result/Time Column -->
                  <td class="col-result" (click)="entry.status !== 'scratched' ? startEdit(entry, isTimeBased ? 'time' : 'points') : null">
                    @if (isEditing(entry, isTimeBased ? 'time' : 'points')) {
                      <input
                        type="text"
                        class="inline-input"
                        [value]="editValue"
                        (input)="editValue = $any($event.target).value"
                        (keydown)="onInputKeydown($event, entry, isTimeBased ? 'time' : 'points')"
                        (blur)="onInputBlur(entry, isTimeBased ? 'time' : 'points')"
                        (click)="$event.stopPropagation()"
                        placeholder="0.000"
                        autofocus>
                    } @else if (getResultStatus(entry) === 'dnf' || getResultStatus(entry) === 'dns' || getResultStatus(entry) === 'dq') {
                      <span class="status-badge {{ getResultStatus(entry) }}">{{ getResultStatus(entry)?.toUpperCase() }}</span>
                    } @else if (getFinalValue(entry)) {
                      <span class="result-value">{{ getFinalValue(entry) }}</span>
                    } @else if (entry.status !== 'scratched') {
                      <span class="click-to-edit">
                        <i class="fa fa-pencil"></i>
                      </span>
                    } @else {
                      <span class="scratched-label">-</span>
                    }
                  </td>

                  <!-- Notes Column -->
                  <td class="col-notes" (click)="entry.status !== 'scratched' ? startEditNotes(entry) : null">
                    @if (isEditingNotes(entry)) {
                      <input
                        type="text"
                        class="inline-input notes-input"
                        [value]="editNotesValue"
                        (input)="editNotesValue = $any($event.target).value"
                        (keydown)="onNotesKeydown($event, entry)"
                        (blur)="onNotesBlur(entry)"
                        (click)="$event.stopPropagation()"
                        placeholder="{{ 'liveSession.addNote' | translate }}"
                        autofocus>
                    } @else if (getNotes(entry)) {
                      <span class="notes-text" [title]="getNotes(entry)">{{ getNotes(entry) }}</span>
                    } @else if (entry.status !== 'scratched') {
                      <span class="click-to-edit">
                        <i class="fa fa-comment"></i>
                      </span>
                    }
                  </td>

                  <!-- Image Column -->
                  <td class="col-image">
                    @if (getResultImage(entry)) {
                      <div class="image-preview" (click)="viewImage(entry)">
                        <img [src]="getResultImage(entry)" alt="Result image">
                        <button class="remove-image-btn" (click)="removeImage(entry); $event.stopPropagation()" title="{{ 'liveSession.removeImage' | translate }}">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                    } @else if (entry.status !== 'scratched') {
                      <button class="add-image-btn" (click)="openImageUpload(entry)">
                        <i class="fa fa-camera"></i>
                      </button>
                    }
                    <input
                      type="file"
                      #imageInput
                      class="hidden-file-input"
                      accept="image/*"
                      (change)="onImageSelected($event, entry)">
                  </td>

                  <!-- Actions -->
                  <td class="col-actions">
                    @if (entry.status !== 'scratched') {
                      <div class="action-buttons-cell">
                        @if (entry.entry_id === currentEntry?.entry_id) {
                          <button class="next-btn" (click)="goNext()" [disabled]="!canGoNext">
                            {{ isLastEntry ? ('liveSession.finish' | translate) : ('liveSession.next' | translate) }}
                            <i class="fa fa-chevron-right"></i>
                          </button>
                        }
                        <button class="status-btn" [class.active]="getResultStatus(entry) === 'dnf'" (click)="setResultStatus(entry, 'dnf')" title="DNF">
                          DNF
                        </button>
                        <button class="status-btn" [class.active]="getResultStatus(entry) === 'dns'" (click)="setResultStatus(entry, 'dns')" title="DNS">
                          DNS
                        </button>
                        <button class="status-btn" [class.active]="getResultStatus(entry) === 'dq'" (click)="setResultStatus(entry, 'dq')" title="DQ">
                          DQ
                        </button>
                      </div>
                    }
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>
